<template>
  <modal v-show="visible" @close="closeModal" footer modalIsLarge>
    <template v-slot:title>
      <h5>Open Kiddies Account — Step {{ step }} of 3</h5>
    </template>
    <template v-slot:body>
      <!-- Step 1: Parent -->
      <div v-if="step === 1">
        <div class="row">
          <div class="col-md-8">
            <div class="form-group">
              <label>BVN</label>
              <input
                type="text"
                class="form-control"
                v-model="parentForm.bvn"
                maxlength="11"
                placeholder="Enter 11-digit BVN"
              />
            </div>
          </div>
          <div class="col-md-4">
            <div class="form-group">
              <label>&nbsp;</label>
              <button
                class="btn btn-primary form-control"
                :disabled="bvnLoading"
                @click.prevent="fetchBvnDetails"
              >
                <span v-if="bvnLoading">Fetching...</span>
                <span v-else>Get BVN Detail</span>
              </button>
            </div>
          </div>
        </div>

        <div v-if="bvnDetails" class="row mb-3 align-items-start">
          <div class="col-md-6">
            <div class="form-group">
              <label>Full Name (from BVN)</label>
              <input
                type="text"
                class="form-control"
                :value="bvnDetails.full_name"
                readonly
              />
            </div>
            <div class="form-group mb-0" v-if="bvnDetails.dob">
              <label>Date of Birth</label>
              <input
                type="text"
                class="form-control"
                :value="bvnDetails.dob"
                readonly
              />
            </div>
          </div>
          <div class="col-md-6">
            <label>BVN Image</label>
            <div class="identity-image-wrap text-center p-2 border rounded">
              <img
                v-if="bvnImageSrc"
                :src="bvnImageSrc"
                alt="BVN"
                class="img-fluid identity-image"
              />
              <span v-else class="text-muted">No BVN image available</span>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <div class="form-group">
              <label>Residential Address</label>
              <input
                type="text"
                class="form-control"
                v-model="parentForm.address"
                placeholder="Residential address"
              />
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label>Phone Number</label>
              <input
                type="text"
                class="form-control"
                v-model="parentForm.phone_number"
                placeholder="Phone number"
              />
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label>Email (optional)</label>
              <input
                type="email"
                class="form-control"
                v-model="parentForm.email"
                placeholder="Email address"
              />
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label>Gender</label>
              <select class="form-control" v-model="parentForm.gender">
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label>Relationship with Child</label>
              <select
                class="form-control"
                v-model="parentForm.relationship_with_child"
              >
                <option value="">Select relationship</option>
                <option value="father">Father</option>
                <option value="mother">Mother</option>
                <option value="guardian">Guardian</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label>ID Type (optional)</label>
              <select class="form-control" v-model="parentForm.id_type">
                <option value="">None</option>
                <option value="national_id">National ID card</option>
                <option value="drivers_license">Driver's license</option>
                <option value="voters_card">Voter's card</option>
                <option value="international_passport">International passport</option>
              </select>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label>ID Document (optional)</label>
              <input
                type="file"
                class="form-control"
                accept="image/*,.pdf"
                @change="onIdFilePicked"
              />
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label>Parent Passport Photo (required)</label>
              <input
                type="file"
                class="form-control"
                accept="image/*"
                @change="onParentPassportPicked"
              />
              <img
                v-if="parentPassportPreview"
                :src="parentPassportPreview"
                class="img-thumbnail mt-2"
                style="max-height: 100px;"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Step 2: Child -->
      <div v-if="step === 2">
        <div class="row">
          <div class="col-md-8">
            <div class="form-group">
              <label>Child's NIN</label>
              <input
                type="text"
                class="form-control"
                v-model="childForm.nin"
                maxlength="11"
                placeholder="Enter 11-digit NIN"
              />
            </div>
          </div>
          <div class="col-md-4">
            <div class="form-group">
              <label>&nbsp;</label>
              <button
                class="btn btn-primary form-control"
                :disabled="ninLoading"
                @click.prevent="fetchNinDetails"
              >
                <span v-if="ninLoading">Fetching...</span>
                <span v-else>Get NIN Detail</span>
              </button>
            </div>
          </div>
        </div>

        <div v-if="ninDetails" class="row mb-3 align-items-start">
          <div class="col-md-6">
            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label>Full Name</label>
                  <input
                    type="text"
                    class="form-control"
                    :value="ninDetails.full_name"
                    readonly
                  />
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label>Age</label>
                  <input
                    type="text"
                    class="form-control"
                    :value="ninDetails.age"
                    readonly
                  />
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <label>NIN Image</label>
            <div class="identity-image-wrap text-center p-2 border rounded">
              <img
                v-if="ninImageSrc"
                :src="ninImageSrc"
                alt="NIN"
                class="img-fluid identity-image"
              />
              <span v-else class="text-muted">No NIN image available</span>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <div class="form-group">
              <label>Gender</label>
              <select class="form-control" v-model="childForm.gender">
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label>School Name</label>
              <input
                type="text"
                class="form-control"
                v-model="childForm.school_name"
              />
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label>Class</label>
              <input type="text" class="form-control" v-model="childForm.class" />
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label>Grade</label>
              <input type="text" class="form-control" v-model="childForm.grade" />
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label>Child Passport Photo (required)</label>
              <input
                type="file"
                class="form-control"
                accept="image/*"
                @change="onChildPassportPicked"
              />
              <img
                v-if="childPassportPreview"
                :src="childPassportPreview"
                class="img-thumbnail mt-2"
                style="max-height: 100px;"
              />
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label>Delivery Method</label>
              <input
                type="text"
                class="form-control"
                value="Pickup"
                readonly
              />
            </div>
          </div>
        </div>

        <div class="form-group mt-2">
          <label>Card Design</label>
          <div class="row">
            <div
              class="col-md-3 col-6 mb-3"
              v-for="design in cardDesigns"
              :key="design.id"
            >
              <div
                class="card-design-option p-2 border rounded"
                :class="{ selected: selectedDesign && selectedDesign.id === design.id }"
                @click="selectDesign(design)"
                style="cursor: pointer;"
              >
                <img
                  :src="design.image"
                  :alt="design.name"
                  class="img-fluid"
                />
                <small class="d-block text-center mt-1">{{ design.name }}</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Step 3: Preview -->
      <div v-if="step === 3 && previewData">
        <h6 class="mb-3">Parent / Guardian</h6>
        <div class="row mb-3">
          <div class="col-md-6"><strong>Name:</strong> {{ previewData.parent.bvn_data.full_name }}</div>
          <div class="col-md-6"><strong>BVN:</strong> {{ previewData.parent.bvn }}</div>
          <div class="col-md-6"><strong>Phone:</strong> {{ previewData.parent.phone_number }}</div>
          <div class="col-md-6"><strong>Email:</strong> {{ previewData.parent.email || 'N/A' }}</div>
          <div class="col-md-6"><strong>Address:</strong> {{ previewData.parent.address }}</div>
          <div class="col-md-6"><strong>Gender:</strong> {{ previewData.parent.gender }}</div>
          <div class="col-md-6"><strong>Relationship:</strong> {{ previewData.parent.relationship_with_child }}</div>
          <div class="col-md-6" v-if="previewData.parent.parent_passport">
            <strong>Parent Passport:</strong>
            <img :src="previewData.parent.parent_passport" class="img-thumbnail d-block mt-1" style="max-height: 80px;" />
          </div>
        </div>

        <h6 class="mb-3">Child</h6>
        <div class="row mb-3">
          <div class="col-md-6"><strong>Name:</strong> {{ previewData.child.nin_data.full_name }}</div>
          <div class="col-md-6"><strong>NIN:</strong> {{ previewData.child.nin }}</div>
          <div class="col-md-6"><strong>Age:</strong> {{ previewData.child.nin_data.age }}</div>
          <div class="col-md-6"><strong>Gender:</strong> {{ previewData.child.gender }}</div>
          <div class="col-md-6"><strong>School:</strong> {{ previewData.child.school_name }}</div>
          <div class="col-md-6"><strong>Class:</strong> {{ previewData.child.class }}</div>
          <div class="col-md-6"><strong>Grade:</strong> {{ previewData.child.grade }}</div>
          <div class="col-md-6"><strong>Delivery:</strong> Pickup</div>
          <div class="col-md-6" v-if="previewData.child.child_passport">
            <strong>Child Passport:</strong>
            <img :src="previewData.child.child_passport" class="img-thumbnail d-block mt-1" style="max-height: 80px;" />
          </div>
          <div class="col-md-6" v-if="previewData.child.card_image">
            <strong>Card Design:</strong>
            <img :src="previewData.child.card_image" class="img-thumbnail d-block mt-1" style="max-height: 80px;" />
          </div>
        </div>
      </div>
    </template>

    <template v-slot:footer>
      <button
        v-if="step > 1"
        class="btn btn-secondary mr-2"
        :disabled="formLoading"
        @click.prevent="goBack"
      >
        Back
      </button>
      <button
        class="btn btn-primary"
        :disabled="formLoading"
        @click.prevent="formAction"
      >
        <span v-if="formLoading">
          {{ step === 3 ? 'Submitting...' : 'Saving...' }}
        </span>
        <span v-else>
          {{ step === 3 ? 'Submit' : 'Next' }}
        </span>
      </button>
      <button class="btn btn-secondary" @click="closeModal">Cancel</button>
    </template>
  </modal>
</template>

<script>
import ApiService from "@/services/api.service";
import ErrorService from "@/services/error.service";
import Swal from "sweetalert2";

export default {
  name: "KiddiesOnboardingModal",
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      step: 1,
      formLoading: false,
      bvnLoading: false,
      ninLoading: false,
      reference: null,
      bvnDetails: null,
      ninDetails: null,
      previewData: null,
      cardDesigns: [],
      selectedDesign: null,
      parentPassportFile: null,
      parentPassportPreview: null,
      idFile: null,
      childPassportFile: null,
      childPassportPreview: null,
      parentForm: {
        bvn: "",
        address: "",
        phone_number: "",
        email: "",
        gender: "",
        relationship_with_child: "",
        id_type: ""
      },
      childForm: {
        nin: "",
        gender: "",
        school_name: "",
        class: "",
        grade: ""
      }
    };
  },
  computed: {
    bvnImageSrc() {
      return this.formatIdentityImage(this.bvnDetails && this.bvnDetails.image);
    },
    ninImageSrc() {
      return this.formatIdentityImage(this.ninDetails && this.ninDetails.image);
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.loadCardDesigns();
      }
    }
  },
  methods: {
    resetForm() {
      this.step = 1;
      this.reference = null;
      this.bvnDetails = null;
      this.ninDetails = null;
      this.previewData = null;
      this.selectedDesign = null;
      this.parentPassportFile = null;
      this.parentPassportPreview = null;
      this.idFile = null;
      this.childPassportFile = null;
      this.childPassportPreview = null;
      this.parentForm = {
        bvn: "",
        address: "",
        phone_number: "",
        email: "",
        gender: "",
        relationship_with_child: "",
        id_type: ""
      };
      this.childForm = {
        nin: "",
        gender: "",
        school_name: "",
        class: "",
        grade: ""
      };
    },
    closeModal() {
      this.resetForm();
      this.$emit("close");
    },
    goBack() {
      if (this.step > 1) this.step--;
    },
    showError(message) {
      ErrorService.displayErrorAlert({ message });
    },
    formatIdentityImage(img) {
      if (!img) return "";
      const value = String(img).trim();
      if (value.startsWith("data:") || value.startsWith("http")) {
        return value;
      }
      return "data:image/jpeg;base64," + value;
    },
    async loadCardDesigns() {
      try {
        const res = await ApiService.get("card/kiddies_card_designs");
        this.cardDesigns = res.data.data || [];
      } catch (error) {
        ErrorService.displayErrorAlert(
          (error.response && error.response.data) || {
            message: "Failed to load card designs"
          }
        );
      }
    },
    selectDesign(design) {
      this.selectedDesign = design;
    },
    onParentPassportPicked(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.parentPassportFile = file;
      const reader = new FileReader();
      reader.onload = e => {
        this.parentPassportPreview = e.target.result;
      };
      reader.readAsDataURL(file);
    },
    onIdFilePicked(event) {
      this.idFile = event.target.files[0] || null;
    },
    onChildPassportPicked(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.childPassportFile = file;
      const reader = new FileReader();
      reader.onload = e => {
        this.childPassportPreview = e.target.result;
      };
      reader.readAsDataURL(file);
    },
    async fetchBvnDetails() {
      if (!this.parentForm.bvn || this.parentForm.bvn.length !== 11) {
        this.showError("Please enter a valid 11-digit BVN");
        return;
      }
      this.bvnLoading = true;
      try {
        const res = await ApiService.post(
          "customer/admin-kiddies-onboarding/verify-bvn",
          { bvn: this.parentForm.bvn }
        );
        this.bvnDetails = res.data.data;
      } catch (error) {
        ErrorService.displayErrorAlert(
          (error.response && error.response.data) || {
            message: "BVN verification failed"
          }
        );
      } finally {
        this.bvnLoading = false;
      }
    },
    async fetchNinDetails() {
      if (!this.childForm.nin || this.childForm.nin.length !== 11) {
        this.showError("Please enter a valid 11-digit NIN");
        return;
      }
      this.ninLoading = true;
      try {
        const res = await ApiService.post(
          "customer/admin-kiddies-onboarding/verify-nin",
          { nin: this.childForm.nin }
        );
        this.ninDetails = res.data.data;
        if (this.ninDetails.gender) {
          const g = String(this.ninDetails.gender).toLowerCase();
          if (g.includes("female")) this.childForm.gender = "female";
          else if (g.includes("male")) this.childForm.gender = "male";
        }
      } catch (error) {
        ErrorService.displayErrorAlert(
          (error.response && error.response.data) || {
            message: "NIN verification failed"
          }
        );
      } finally {
        this.ninLoading = false;
      }
    },
    validateStep1() {
      if (!this.bvnDetails) {
        this.showError("Please fetch BVN details first");
        return false;
      }
      if (!this.parentForm.address || !this.parentForm.phone_number) {
        this.showError("Address and phone number are required");
        return false;
      }
      if (!this.parentForm.gender || !this.parentForm.relationship_with_child) {
        this.showError("Gender and relationship are required");
        return false;
      }
      if (!this.parentPassportFile) {
        this.showError("Parent passport photo is required");
        return false;
      }
      return true;
    },
    validateStep2() {
      if (!this.ninDetails) {
        this.showError("Please fetch NIN details first");
        return false;
      }
      if (
        !this.childForm.gender ||
        !this.childForm.school_name ||
        !this.childForm.class ||
        !this.childForm.grade
      ) {
        this.showError("All child fields are required");
        return false;
      }
      if (!this.childPassportFile) {
        this.showError("Child passport photo is required");
        return false;
      }
      if (!this.selectedDesign) {
        this.showError("Please select a card design");
        return false;
      }
      return true;
    },
    async submitStepOne() {
      const formData = new FormData();
      if (this.reference) formData.append("reference", this.reference);
      formData.append("bvn", this.parentForm.bvn);
      formData.append("address", this.parentForm.address);
      formData.append("phone_number", this.parentForm.phone_number);
      if (this.parentForm.email) formData.append("email", this.parentForm.email);
      formData.append("gender", this.parentForm.gender);
      formData.append("relationship_with_child", this.parentForm.relationship_with_child);
      if (this.parentForm.id_type) formData.append("id_type", this.parentForm.id_type);
      if (this.idFile) formData.append("id_file", this.idFile);
      formData.append("parent_passport", this.parentPassportFile);

      const res = await ApiService.post(
        "customer/admin-kiddies-onboarding/step-one",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      this.reference = res.data.data.reference;
      this.step = 2;
    },
    async submitStepTwo() {
      const formData = new FormData();
      formData.append("reference", this.reference);
      formData.append("nin", this.childForm.nin);
      formData.append("gender", this.childForm.gender);
      formData.append("school_name", this.childForm.school_name);
      formData.append("class", this.childForm.class);
      formData.append("grade", this.childForm.grade);
      formData.append("card_image", this.selectedDesign.image);
      formData.append("image_dim", this.selectedDesign.orientation || "portrait");
      formData.append("delivery_method", "pickup");
      formData.append("child_passport", this.childPassportFile);

      await ApiService.post(
        "customer/admin-kiddies-onboarding/step-two",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const previewRes = await ApiService.get(
        `customer/admin-kiddies-onboarding/${this.reference}`
      );
      this.previewData = previewRes.data.data;
      this.step = 3;
    },
    async submitFinalize() {
      const res = await ApiService.post(
        "customer/admin-kiddies-onboarding/finalize",
        { reference: this.reference }
      );

      const creds = res.data.data.credentials;
      const kiddiesRef = res.data.data.kiddies_account.reference;
      const successMessage = res.data.message;

      this.$emit("success");
      this.closeModal();

      await Swal.fire({
        title: "Success",
        html: `
          <p>${successMessage}</p>
          <p><strong>Username:</strong> ${creds.username}</p>
        `,
        icon: "success",
        showCancelButton: true,
        confirmButtonText: "View Kiddies Account",
        cancelButtonText: "Close",
        customClass: {
          container: "swal-on-top"
        }
      }).then(result => {
        if (result.isConfirmed) {
          this.$router.push({ path: `/kiddies/${kiddiesRef}` });
        }
      });
    },
    async formAction() {
      if (this.step === 1) {
        if (!this.validateStep1()) return;
        this.formLoading = true;
        try {
          await this.submitStepOne();
        } catch (error) {
          ErrorService.displayErrorAlert(
            (error.response && error.response.data) || {
              message: "Something went wrong"
            }
          );
        } finally {
          this.formLoading = false;
        }
      } else if (this.step === 2) {
        if (!this.validateStep2()) return;
        this.formLoading = true;
        try {
          await this.submitStepTwo();
        } catch (error) {
          ErrorService.displayErrorAlert(
            (error.response && error.response.data) || {
              message: "Something went wrong"
            }
          );
        } finally {
          this.formLoading = false;
        }
      } else if (this.step === 3) {
        this.formLoading = true;
        try {
          await this.submitFinalize();
        } catch (error) {
          ErrorService.displayErrorAlert(
            (error.response && error.response.data) || {
              message: "Something went wrong"
            }
          );
        } finally {
          this.formLoading = false;
        }
      }
    }
  }
};
</script>

<style scoped>
.card-design-option.selected {
  border-color: #007bff !important;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.identity-image-wrap {
  min-height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
}

.identity-image {
  max-height: 200px;
  width: auto;
  object-fit: contain;
}
</style>

<style>
.swal-on-top {
  z-index: 200000 !important;
}
</style>
